import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiSearchLine, RiDownloadLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  InfraCodeBlock,
  InfraField,
  InfraSection,
  InfraStack,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const SplunkInfoCard = () => {
  const { entity } = useEntity();

  const annotations = entity.metadata.annotations || {};

  const webUrl = annotations['splunk.infrastructure/web-url'];
  const hecUrl = annotations['splunk.infrastructure/hec-url'];
  const mgmtUrl = annotations['splunk.infrastructure/mgmt-url'];
  const adminUsername = annotations['splunk.infrastructure/admin-username'];
  const sshPrivateKey = annotations['splunk.infrastructure/ssh-private-key'];
  const sshCommand = annotations['splunk.infrastructure/ssh-command'];
  const ssmCommand = annotations['splunk.infrastructure/ssm-command'];

  const instanceId = annotations['aws.infrastructure/instance-id'];
  const publicIp = annotations['aws.infrastructure/public-ip'];
  const region = annotations['aws.infrastructure/region'];
  const templateType = annotations['infrastructure.backstage.io/type'];
  const environmentName = annotations['infrastructure.backstage.io/environment-name'];

  if (templateType !== 'aws-splunk') {
    return null;
  }

  const downloadSshKey = () => {
    if (!sshPrivateKey) return;

    try {
      const decodedKey = atob(sshPrivateKey);
      const blob = new Blob([decodedKey], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'splunk-key.pem';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download SSH key:', error);
    }
  };

  const getAwsConsoleUrl = () => {
    const awsRegion = region || 'us-east-1';
    return `https://console.aws.amazon.com/ec2/v2/home?region=${awsRegion}#InstanceDetails:instanceId=${instanceId}`;
  };

  const hecTestCommand = hecUrl
    ? `curl -k ${hecUrl} -H "Authorization: Splunk <token>" -d '{"event": "test"}'`
    : '';

  return (
    <InfoCard
      title="🔍 Splunk Enterprise"
      subheader={`Environment: ${environmentName || 'N/A'}`}
      icon={<RiSearchLine size={20} />}
    >
      <InfraStack>
        <InfraSection title="🌐 Splunk Web UI">
          {webUrl && (
            <InfraField
              label="Web UI URL:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={webUrl}>{webUrl}</ExternalLink>
                  <CopyButton text={webUrl} />
                </span>
              }
              mono={false}
            />
          )}
          {adminUsername && <CopyableValue label="Username:" value={adminUsername} />}
        </InfraSection>

        {hecUrl && (
          <InfraSection title="📡 HTTP Event Collector (HEC)">
            <InfraField
              label="HEC URL:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={hecUrl}>{hecUrl}</ExternalLink>
                  <CopyButton text={hecUrl} />
                </span>
              }
              mono={false}
            />
            <InfraField label="Test HEC:" value="" mono={false} />
            <div style={{ position: 'relative' }}>
              <InfraCodeBlock variant="light">
                curl -k {hecUrl} \<br />
                &nbsp;&nbsp;-H &quot;Authorization: Splunk &lt;token&gt;&quot; \<br />
                &nbsp;&nbsp;-d &#123;&quot;event&quot;: &quot;test&quot;&#125;
              </InfraCodeBlock>
              {hecTestCommand && (
                <div style={{ position: 'absolute', top: 4, right: 4 }}>
                  <CopyButton text={hecTestCommand} />
                </div>
              )}
            </div>
          </InfraSection>
        )}

        {mgmtUrl && (
          <InfraSection title="⚙️ Management API">
            <InfraField
              label="API URL:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={mgmtUrl}>{mgmtUrl}</ExternalLink>
                  <CopyButton text={mgmtUrl} />
                </span>
              }
              mono={false}
            />
          </InfraSection>
        )}

        {(sshCommand || ssmCommand || sshPrivateKey) && (
          <InfraSection title="🔑 SSH Access">
            {sshPrivateKey && (
              <>
                <InfraField label="SSH Private Key:" value="" mono={false} />
                <Button variant="secondary" size="small" onPress={downloadSshKey}>
                  <RiDownloadLine size={16} />
                  Download splunk-key.pem
                </Button>
                <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                  After download: chmod 400 splunk-key.pem
                </Text>
              </>
            )}
            {sshCommand && <CopyableValue label="SSH Command:" value={sshCommand} />}
            {ssmCommand && (
              <>
                <CopyableValue label="SSM Command:" value={ssmCommand} />
                <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                  Recommended: No SSH key needed
                </Text>
              </>
            )}
          </InfraSection>
        )}

        {instanceId && (
          <InfraSection title="☁️ AWS Infrastructure">
            <InfraField
              label="EC2 Instance:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={getAwsConsoleUrl()}>{instanceId}</ExternalLink>
                  <CopyButton text={instanceId} />
                </span>
              }
              mono={false}
            />
            {publicIp && <CopyableValue label="Public IP:" value={publicIp} />}
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
